import React from "react";
import { useNavigate } from "react-router-dom";
import { formatGBP } from "@/lib/marketplace";
import { ShoppingBag, Package2, Truck, ChevronRight } from "lucide-react";

/**
 * Iteration 139/140/143 — SharedTransactionRow.
 *
 * A single canonical table row shared between Parent Dashboard
 * (History Listings) and Kid Zone (My Shop Ledger → Orders tab).
 *
 * Columns: [ Item | Title | Price | Posted | Status | Seller ]
 *
 * Props:
 *   - row: normalized ledger row from `/api/kids/{id}/orders` or
 *     `/api/parent/ledger` — must include `role`, `seller_name`,
 *     `item_title`, `price_pennies`, `net_pennies`, `status`, `created_at`,
 *     `fulfillment_method` (optional)
 *   - viewerRole: "parent" | "kid" — determines whether the row shows
 *     the buyer shipping address column and the dispatch action.
 *   - testIdPrefix: string prefix for `data-testid` attributes.
 *   - onOpenSummary: (orderId) => void — parent-only click handler that
 *     opens the Order Summary drawer. When omitted, no action button
 *     renders.
 */
export function SharedTransactionRow({ row, viewerRole = "kid", testIdPrefix = "shared-txn", onOpenSummary }) {
  const navigate = useNavigate();
  const isSold = row.role === "sold";
  const priceDisplay = isSold
    ? (row.net_pennies ?? row.price_pennies ?? 0)
    : (row.price_pennies ?? row.gross_pennies ?? 0);
  const tagText = isSold ? "Sold" : "Bought";
  const tagClass = isSold
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : "bg-sky-100 text-sky-800 border-sky-200";
  const IconEl = isSold ? Package2 : ShoppingBag;
  const posted = (row.created_at || "").slice(0, 10);

  // Iteration 143 — Dispatch pill + action button. Parent viewer only,
  // and only for sold rows whose fulfillment involves postage. When
  // upstream doesn't stamp a fulfillment_method we still surface the
  // pill for `paid` / `shipped` orders so parents can dispatch.
  const status = (row.status || "").toLowerCase();
  const fulfillment = (row.fulfillment_method || "").toLowerCase();
  const isShippingOrder = ["shipping", "home_delivery", ""].includes(fulfillment);
  const showDispatch = viewerRole === "parent" && isSold && isShippingOrder && ["paid", "shipped"].includes(status);

  let statusPillClass = "bg-slate-100 text-slate-700";
  let statusPillLabel = row.status || "—";
  if (showDispatch && status === "paid") {
    statusPillClass = "bg-amber-100 text-amber-900 border border-amber-200";
    statusPillLabel = "Awaiting Shipping";
  } else if (showDispatch && status === "shipped") {
    statusPillClass = "bg-emerald-100 text-emerald-800 border border-emerald-200";
    statusPillLabel = "Shipped";
  }

  // Iteration 144 — every ledger row is now a tap-through link to the
  // canonical Order Detail page (`/orders/:id`). Prefer `order_id`
  // (some upstream shapes), then `metadata.order_id`, else fall back
  // to the row's own id.
  const orderId = row.order_id || row.metadata?.order_id || row.id;

  const goToDetail = () => {
    if (orderId) navigate(`/orders/${orderId}`);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToDetail();
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={handleKeyDown}
      className="grid grid-cols-12 gap-3 items-center rounded-2xl border border-slate-200 bg-white px-4 py-3 hover:border-brandSlate hover:shadow-sm transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-brandSlate/40"
      data-testid={`${testIdPrefix}-row-${row.id}`}
    >
      {/* Item icon */}
      <div className="col-span-1 flex items-center justify-center">
        <div
          className={`w-9 h-9 rounded-xl inline-flex items-center justify-center ${
            isSold ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"
          }`}
          data-testid={`${testIdPrefix}-icon-${row.id}`}
        >
          <IconEl className="w-4 h-4" />
        </div>
      </div>

      {/* Title */}
      <div className="col-span-4 min-w-0">
        <div
          className="font-semibold text-sm text-slate-900 truncate"
          data-testid={`${testIdPrefix}-title-${row.id}`}
          title={row.item_title}
        >
          {row.item_title || "Item"}
        </div>
        <span
          className={`inline-flex items-center mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${tagClass}`}
          data-testid={`${testIdPrefix}-tag-${row.id}`}
        >
          {tagText}
        </span>
        {row.sponsored_purchase && (
          <span
            className="inline-flex items-center mt-0.5 ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-100 text-amber-900 border-amber-200"
            data-testid={`${testIdPrefix}-sponsored-${row.id}`}
          >
            🌟 Bought by Community Sponsorship Purchase Fund
          </span>
        )}
      </div>

      {/* Price */}
      <div className="col-span-2 text-right">
        <div className="text-brandPinkDark font-semibold whitespace-nowrap" data-testid={`${testIdPrefix}-price-${row.id}`}>
          {formatGBP(priceDisplay / 100)}
        </div>
        {/* Iteration 142 — buyer platform fee subtext is a PARENT-only
            surface (system-charge tracking). Kids never see it inside
            My Shop Ledger — the £0.30 is Tom's buyer fee, not Kat's
            payout deduction. */}
        {viewerRole === "parent" && isSold && (row.fee_pennies || 0) > 0 && (
          <div className="text-[10px] text-slate-500" data-testid={`${testIdPrefix}-price-note-${row.id}`}>net · fee £{((row.fee_pennies || 0) / 100).toFixed(2)}</div>
        )}
      </div>

      {/* Posted date */}
      <div className="col-span-2 text-xs text-slate-600" data-testid={`${testIdPrefix}-posted-${row.id}`}>
        {posted || "—"}
      </div>

      {/* Status */}
      <div className="col-span-1 text-[11px]" data-testid={`${testIdPrefix}-status-${row.id}`}>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full capitalize ${statusPillClass}`}>
          {statusPillLabel}
        </span>
      </div>

      {/* Seller */}
      <div className="col-span-2 min-w-0 text-sm text-slate-700 truncate" data-testid={`${testIdPrefix}-seller-${row.id}`} title={row.seller_name || "—"}>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block leading-none">Seller</span>
        <span className="font-medium">{row.seller_name || "—"}</span>
      </div>

      {/* Parent-only — dispatch action button (Iteration 143) */}
      {showDispatch && typeof onOpenSummary === "function" && (
        <div className="col-span-12 md:col-span-12 flex md:justify-end mt-2">
          {status === "paid" ? (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenSummary(orderId); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white"
              data-testid={`${testIdPrefix}-ship-cta-${row.id}`}
            >
              <Truck className="w-3.5 h-3.5" /> View Buyer Info &amp; Ship
            </button>
          ) : null}
        </div>
      )}

      {/* Parent-only — buyer shipping address on wide screens */}
      {viewerRole === "parent" && row.buyer_shipping_address && (
        <div className="col-span-12 md:col-span-12 mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500" data-testid={`${testIdPrefix}-buyer-addr-${row.id}`}>
          <span className="font-bold uppercase tracking-wider text-slate-400 mr-1">Ships to:</span>
          {[row.buyer_shipping_address.line1, row.buyer_shipping_address.city, row.buyer_shipping_address.postcode].filter(Boolean).join(" · ")}
        </div>
      )}

      {/* Iteration 144 — canonical "View order details" affordance on
          every row. The whole card is a tap-through — this text just
          makes the affordance discoverable on both scopes (Full
          Household View + per-kid). */}
      <div className="col-span-12 md:col-span-12 flex justify-end mt-2 text-brandPinkDark text-xs font-bold" data-testid={`${testIdPrefix}-view-details-${row.id}`}>
        View order details <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
      </div>
    </div>
  );
}

export default SharedTransactionRow;
